import React, { useContext, useState, useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';

import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';

import Button from '@prisjakt/ui-button';
import Lazy from '@prisjakt/ui-lazy';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import RouteContext from '../../../../routes/context';

import Placeholder from './placeholder';
import PriceFilters from './price-filters/price-filters';
import MissingShop from '../missing-shop';
import PriceInformation from '../price-information';
import PriceRow, { columnCount } from '../../../../components/price-row';
import SortOptions from '../sort-options';
import VariantGroups from '../variant-groups';
import ViewportContext from '../../../../components/viewport/context';

import {
  productPriceClick,
  productClickOutBounce,
  priceShippingSort,
  setSelectedVariant,
} from '../../actions/product';

import { AppState } from '../../../../reducers';

import { initialFilterState } from '../../constants';

import { getFilter, getValidPriceType } from './utils';
import { sortByPrice } from '../sort-options/utils';

import {
  trackPriceListFilterUpdate,
  trackProductVariantClick,
} from '../tracking';

import {
  BadgesFragment,
  Price,
  Product,
  ProductPageFragment,
  ProductMediaFragment,
  User,
  Variant as VariantType,
  VariantGroup,
} from '../../../../types';
import { CurrentCampaign, ProductPriceType } from '../../types';
import { SortOption, SortOptionEnum } from '../sort-options/types';

import * as S from './styles';

type OwnProps = {
  id: number;
  adOffset?: number;
  brand?: ProductPageFragment['brand'] | null;
  campaign?: CurrentCampaign | null;
  category?: ProductPageFragment['category'] | null;
  media: ProductMediaFragment['media'] & { first?: string | null };
  prices?: Product['prices'];
  productName?: string;
  storeBadges?: BadgesFragment['storeBadges'];
  visiblePrices?: number;
  variantGroups: VariantGroup[] | null;
};

type StateProps = {
  me: User;
  selectedVariant?: VariantType | null;
  currentSort: SortOption;
};

type DispatchProps = {
  dispatchProductPriceClick: (
    id: number,
    price: Price | number,
    featured: boolean,
    status: string,
    isAlternative: boolean
  ) => void;
  dispatchProductClickOutBounce: (id: number, priceId?: string | null) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const ADS_INDEXES = [
  3,
  9,
  15,
  // Ad every 10th price row
  ...Array(15)
    .fill(10)
    .map((v, i) => v + v * i + 15),
];

const PriceList = ({
  id,
  adOffset = 0,
  brand,
  campaign,
  category,
  me,
  media,
  prices,
  productName,
  storeBadges = [],
  visiblePrices = 50,
  dispatchProductPriceClick,
  dispatchProductClickOutBounce,
  selectedVariant,
  variantGroups,
  currentSort,
}: Props) => {
  const { t, market } = useContext(AppContext);
  const { isMobile } = useContext(ViewportContext);
  const { dispatch: routeDispatch } = useContext(RouteContext);

  const dispatch = useDispatch();

  const [showMorePrices, setShowMorePrices] = useState(false);

  const [junkFilter, setJunkFilters] = useState(initialFilterState);
  const [featuredAlternativePriceType] = useState<string>(
    market.priceList && market.priceList.featuredAlternativePriceType
      ? market.priceList.featuredAlternativePriceType
      : 'international'
  );
  const [hiddenListOptions] = useState<string[]>(
    market.priceList && market.priceList.hiddenListOptions
      ? [...market.priceList.hiddenListOptions]
      : []
  );

  useEffect(() => {
    if (!prices?.nodes?.length) {
      // If initial fetch returns no prices, fetch prices again with showUsedProducts: true
      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...junkFilter,
          showUsedProducts: true,
        },
        variants: null,
      });

      setJunkFilters({
        ...junkFilter,
        showUsedProducts: true,
      });
    }
  }, []);

  const getSortedPrices = () => {
    const allPrices = prices?.nodes?.filter(x => x) as
      | Price[]
      | null
      | undefined;
    if (
      allPrices?.length &&
      currentSort === SortOptionEnum.PRICE_INCLUDES_SHIPPING
    ) {
      return sortByPrice(currentSort, allPrices);
    }

    return allPrices;
  };

  const sortedPrices: Price[] | undefined | null = getSortedPrices();

  const advertSpacing = { top: '10px', bottom: '10px' };

  const {
    hasInternationalPrices,
    hasUsedProducts,
    hasNotInMintCondition,
    hasPickupOnly,
    hasUnavailable,
  } = getFilter(prices);

  const priceFilters = [
    {
      name: 'showInternational',
      type: ProductPriceType.INTERNATIONAL,
      checked: junkFilter.showInternational,
      hasPrices: hasInternationalPrices,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.INTERNATIONAL,
      label: t('product', 'price-filter.international'),
      shortLabel: t('product', 'price-filter.international-short'),
      hide: false,
    },
    {
      name: 'showUsedProducts',
      type: ProductPriceType.USED_PRODUCT,
      checked: junkFilter.showUsedProducts,
      hasPrices: hasUsedProducts,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.USED_PRODUCT,
      label: t('product', 'price-filter.used'),
      hide: false,
    },
    {
      name: 'showNotInMintCondition',
      type: ProductPriceType.NOT_IN_MINT_CONDITION,
      checked: junkFilter.showNotInMintCondition,
      label: t('product', 'price-filter.not-in-mint-condition'),
      hasPrices: hasNotInMintCondition,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.NOT_IN_MINT_CONDITION
        ).length
      ),
    },
    {
      name: 'showPickupOnly',
      type: ProductPriceType.PICKUP_ONLY,
      checked: junkFilter.showPickupOnly,
      label: t('product', 'price-filter.pickup-only'),
      hasPrices: hasPickupOnly,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.PICKUP_ONLY
        ).length
      ),
    },
    {
      name: 'showUnavailable',
      type: ProductPriceType.UNAVAILABLE,
      checked: junkFilter.showUnavailable,
      label: t('product', 'price-filter.not-available-for-purchase'),
      hasPrices: hasUnavailable,
      showAsQuickFilter: false,
      hide: false,
    },
  ];

  const quickFilters = priceFilters.filter(elem => elem.showAsQuickFilter);

  const handlePriceFilterCheckboxChange = useCallback(
    e => {
      const isQuickFilter = Boolean(
        quickFilters.filter(quickFilter => quickFilter.name === e.target.name)
          .length
      );

      const selectedFilter = priceFilters.find(
        priceFilter => priceFilter.name === e.target.name
      );

      if (selectedFilter && selectedFilter.type)
        trackPriceListFilterUpdate(selectedFilter.type, isQuickFilter);

      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...junkFilter,
          [e.target.name]: e.target.checked,
        },
        // selectedVariant.id === 0 corresponds to "all", which should not be fetched by id
        variants:
          selectedVariant && selectedVariant.id !== 0
            ? [selectedVariant.id]
            : null,
      });

      setJunkFilters({
        ...junkFilter,
        [e.target.name]: e.target.checked,
      });
    },
    [junkFilter, priceFilters, routeDispatch, quickFilters, selectedVariant]
  );

  const getMobileAd = (index: number) => {
    const placementNumber = Math.min(3, index + 1);

    const triggerPointProp = placementNumber > 1 ? { triggerPoint: 100 } : {};

    return (
      <S.AdvertTableRowElement key={`mobile-ad-container-${index}`}>
        <td colSpan={columnCount}>
          <S.AdContainer
            placement={`dubbelpanorama${placementNumber}`}
            format={`panorama_${placementNumber}`}
            min={0}
            max={860}
            spacing={advertSpacing}
            {...triggerPointProp}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const getDesktopAd = (index: number) => {
    const desktopAds = [
      { placement: 'panorama_extra', format: 'panorama_1_wde' },
      {
        placement: 'panorama_extra_2',
        format: 'panorama_2_wde',
        triggerPoint: 100,
      },
      {
        placement: 'panorama_extra_2',
        format: 'panorama_2_wde',
        triggerPoint: 100,
      },
    ].filter(Boolean);
    const adProps = desktopAds[Math.min(desktopAds.length - 1, index)];

    return (
      <S.AdvertTableRowElement
        withPosition
        key={`desktop-ad-container-${index}`}
        className="pj-ui-pricelist--advert"
      >
        <td colSpan={columnCount}>
          <S.AdContainer
            min={860}
            max={10000}
            scalable
            scaleBase=".pj-ui-pricelist--advert"
            spacing={advertSpacing}
            {...adProps}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const insertTableAds = (tableRows: JSX.Element[]) => {
    const adjustedAdsIndexes = ADS_INDEXES.map(
      index => index + adOffset
    ).filter(index => index <= tableRows.length);
    if (!adjustedAdsIndexes.length) {
      // special case for short list
      tableRows.splice(tableRows.length, 0, getDesktopAd(0));
      tableRows.splice(tableRows.length, 0, getMobileAd(0));
      return tableRows;
    }
    for (let i = adjustedAdsIndexes.length - 1; i >= 0; i -= 1) {
      const index = adjustedAdsIndexes[i];

      // Only 3 ads in desktop viewport
      if (i <= 2) {
        tableRows.splice(index, 0, getDesktopAd(i));
      }
      tableRows.splice(index, 0, getMobileAd(i));
    }
    return tableRows;
  };

  const getCampaignById = (item: Price) => {
    return campaign?.price &&
      campaign?.media &&
      campaign?.name &&
      item.id === campaign.price.id
      ? campaign
      : null;
  };

  const lazyPlaceholderStyle = { height: 57 };

  const lazyPlaceholder = (
    <tr>
      <td colSpan={columnCount} style={lazyPlaceholderStyle} />
    </tr>
  );

  const wrapLazy = (
    order: number,
    rowKey: string,
    component: React.ReactElement
  ) => {
    if (order > 10) {
      return (
        <Lazy placeholder={lazyPlaceholder} offset={200} key={rowKey}>
          {component}
        </Lazy>
      );
    }
    return component;
  };

  const renderPriceRow = (item: Price, index: number) => {
    const rowKey = `${item.id}-${index}`;

    return wrapLazy(
      index,
      rowKey,
      <PriceRow
        key={rowKey}
        name={item.name}
        media={media}
        productId={id}
        productName={productName}
        priceId={item.id}
        priceType={getValidPriceType(item.type)}
        price={item.price}
        deliveryOptions={item.deliveryOptions}
        store={item.store}
        storeInfo={
          item.store && item.store.providedByStore
            ? item.store.providedByStore.generalInformation
            : null
        }
        stock={item.stock}
        externalUri={item.externalUri}
        brand={brand}
        alternativePrices={item.alternativePrices}
        openInNewWindow
        brandRecommendedRetailer={Boolean(item.brandRecommendedRetailer)}
        isInternationalPrice={Boolean(
          junkFilter.showInternational &&
            item.store &&
            item.store.market === 'secondary'
        )}
        campaign={getCampaignById(item)}
        onPriceClick={(
          featured: boolean,
          price: number | undefined,
          status: string,
          isAlternative: boolean
        ) => {
          // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
          // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
          dispatchProductPriceClick(
            id,
            price || item,
            featured,
            status,
            isAlternative
          );
        }}
        onClickOutBounce={dispatchProductClickOutBounce}
        currentSort={currentSort}
      />
    );
  };

  const handleSelectedVariant = (
    group: VariantGroup,
    variant: VariantType | null
  ) => {
    dispatch(setSelectedVariant(variant));
    trackProductVariantClick(group, variant);
    routeDispatch({
      type: 'PRODUCT_VARIANTS',
      // variant.id === 0 corresponds to "all", which should not be fetched by id
      variants: variant && variant.id !== 0 ? [variant.id] : null,
      options: { ...junkFilter },
    });
  };

  return (
    <>
      {variantGroups?.length &&
      market.productVariantCategories?.find(c => c === category?.id) ? (
        <VariantGroups
          handleSelectedVariant={handleSelectedVariant}
          variantGroups={variantGroups}
        />
      ) : null}
      <Panel
        header={
          <S.Header>
            <PanelHeader
              title={
                <>
                  <S.OnlyDesktop data-test="SortOrder">
                    {t('product', 'prices.title')}
                    <S.TitleCount>({sortedPrices?.length || 0})</S.TitleCount>
                    {me?.analyticsAccess?.stores && <MissingShop />}
                  </S.OnlyDesktop>
                </>
              }
              divider="none"
            >
              {sortedPrices?.length ? (
                <>
                  <SortOptions
                    prices={sortedPrices as Price[]}
                    compact={isMobile && Boolean(quickFilters.length)}
                    setCurrentSort={sort => dispatch(priceShippingSort(sort))}
                    currentSort={currentSort}
                  />
                </>
              ) : null}

              <S.QuickPriceFilters
                priceFilters={quickFilters}
                onFilterChanged={handlePriceFilterCheckboxChange}
              />

              <S.PriceFilter
                title={
                  <>
                    <S.OnlyMobile>
                      <DotsVerticalIcon size={20} />{' '}
                    </S.OnlyMobile>
                    <S.TabletDesktop>
                      {t('product', 'prices.more-filters')}{' '}
                    </S.TabletDesktop>
                  </>
                }
                type="custom-content-type"
                headerPosition="right"
                ariaLabel={t('product', 'prices.more-filters')}
                data-test="PriceFilters"
              >
                <PriceFilters
                  priceFilters={priceFilters}
                  onFilterChanged={handlePriceFilterCheckboxChange}
                />
              </S.PriceFilter>
            </PanelHeader>
          </S.Header>
        }
        footer={
          <div>
            {market.showProductInformationTexts &&
              sortedPrices &&
              sortedPrices.length > 0 && (
                <S.Information>
                  {t('product', 'price-list.data-information')}
                </S.Information>
              )}
            {!showMorePrices &&
              sortedPrices &&
              sortedPrices.length > visiblePrices && (
                <>
                  <Button
                    style={{ width: '100%' }}
                    iconPosition="bottom"
                    icon={<ChevronDown />}
                    variant="text"
                    color="link"
                    onClick={() => setShowMorePrices(true)}
                  >
                    {t('product', 'prices.show-more-prices-button')}
                  </Button>
                </>
              )}
          </div>
        }
      >
        {prices && prices.meta && prices.meta.storeStatistics && (
          <PriceInformation storeStatistics={prices.meta.storeStatistics} />
        )}

        {sortedPrices && sortedPrices.length > 0 ? (
          <S.PriceRowTable>
            <thead>
              <tr>
                <S.PriceRowTableHeadExpand />
                <S.PriceRowTableHeadStore scope="col">
                  <div>{t('product', 'price-list.header-store')}</div>
                </S.PriceRowTableHeadStore>
                <S.PriceRowTableHeadProduct scope="col">
                  <div>{t('product', 'price-list.header-product')}</div>
                </S.PriceRowTableHeadProduct>
                <S.PriceRowTableHeadStock scope="col">
                  <div>{t('product', 'price-list.header-stock')}</div>
                </S.PriceRowTableHeadStock>
                <S.PriceRowTableHeadPrice scope="col">
                  <div>{t('product', 'price-list.header-price')}</div>
                </S.PriceRowTableHeadPrice>
                <S.PriceRowTableHeadClickOut />
              </tr>
            </thead>

            <tbody>
              {sortedPrices &&
                sortedPrices.length > 0 &&
                insertTableAds(
                  sortedPrices
                    .slice(0, showMorePrices ? undefined : visiblePrices)
                    .map((item, index) => renderPriceRow(item as Price, index))
                )}
            </tbody>
          </S.PriceRowTable>
        ) : (
          <Placeholder category={category} />
        )}
      </Panel>
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  me: state.areas.account.me,
  selectedVariant: state.areas.product.variant,
  currentSort: state.areas.product.sort,
});

const dispatchProps = {
  dispatchProductPriceClick: productPriceClick,
  dispatchProductClickOutBounce: productClickOutBounce,
};

export default connect(mapStateToProps, dispatchProps)(PriceList);
